import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { sendDemoNotifications } from '../utils/demoNotifications';

// Form validation schema
const schema = yup.object({
  name: yup.string().required('Full name is required'),
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  company: yup.string().required('Company name is required'),
  phone: yup.string(),
  event_type: yup.string().required('Please select an event type'),
  event_size: yup.string().required('Please select expected size'),
  event_date: yup.string(),
  message: yup.string().min(10, 'Message must be at least 10 characters')
});

const LandingPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [notificationResults, setNotificationResults] = useState(null);
  const [notificationErrors, setNotificationErrors] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [animatedStats, setAnimatedStats] = useState({
    organizers: 0,
    events: 0,
    attendees: 0
  });

  const heroRef = useRef(null);
  const featuresRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  // Parallax and mouse tracking effects
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Feature carousel auto-rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Animated statistics with easing
  useEffect(() => {
    const animateStats = () => {
      const duration = 3000;
      const steps = 120;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);

        setAnimatedStats({
          organizers: Math.floor(500 * easeOutQuart),
          events: Math.floor(1000 * easeOutQuart),
          attendees: Math.floor(50000 * easeOutQuart)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
        }
      }, stepDuration);

      return () => clearInterval(interval);
    };

    const timer = setTimeout(animateStats, 1500);
    return () => clearTimeout(timer);
  }, []);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setNotificationErrors([]);
    
    try {
      console.log('🚀 Starting notification process...');
      console.log('📋 Form data submitted:', data);
      
      // Send notifications using Possinote
      const results = await sendDemoNotifications(data);
      setNotificationResults(results);
      
      // Comprehensive logging
      console.log('📊 Full notification results:', results);
      console.log('✅ Requester Email:', results.requesterEmail);
      console.log('✅ Sales Team Email:', results.salesTeamEmail);
      console.log('✅ Requester SMS:', results.requesterSMS);
      console.log('✅ Sales Team SMS:', results.salesTeamSMS);
      
      if (results.errors && results.errors.length > 0) {
        console.log('❌ Errors found:', results.errors);
        setNotificationErrors(results.errors);
      }
      
      setSubmitSuccess(true);
      setIsSubmitting(false);
      
      // Remove automatic redirect - let user stay on success page
      // setTimeout(() => {
      //   setSubmitSuccess(false);
      //   setCurrentStep(1);
      //   setNotificationResults(null);
      //   setNotificationErrors([]);
      // }, 6000);
    } catch (error) {
      console.error('💥 Failed to send notifications:', error);
      setNotificationErrors([`Failed to send notifications: ${error.message}`]);
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const features = [
    {
      icon: '🎯',
      title: 'Smart Registration',
      description: 'AI-powered registration that adapts to your event needs',
      color: '#FF6B6B'
    },
    {
      icon: '⚡',
      title: 'Lightning Fast Check-in',
      description: 'QR codes and mobile apps for instant attendee processing',
      color: '#4ECDC4'
    },
    {
      icon: '📊',
      title: 'Real-time Analytics',
      description: 'Live dashboards with insights that matter to your business',
      color: '#45B7D1'
    }
  ];

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {[1, 2, 3].map((step) => (
        <div
          key={step}
          className={`step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
        >
          <div className="step-number">{step}</div>
          <div className="step-label">
            {step === 1 ? 'Basic Info' : step === 2 ? 'Event Details' : 'Additional Info'}
          </div>
        </div>
      ))}
    </div>
  );

  const renderFormStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="form-step">
            <h3>Tell us about yourself</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  {...register('name')}
                  type="text"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.name && <span className="error-message">{errors.name.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  {...register('email')}
                  type="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="error-message">{errors.email.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="company">Company Name *</label>
                <input
                  {...register('company')}
                  type="text"
                  className={`form-input ${errors.company ? 'error' : ''}`}
                  placeholder="Enter your company name"
                />
                {errors.company && <span className="error-message">{errors.company.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  {...register('phone')}
                  type="tel"
                  className="form-input"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>
            <button type="button" className="btn-next" onClick={nextStep}>
              Next Step <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        );

      case 2:
        return (
          <div className="form-step">
            <h3>Event Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="event_type">Event Type *</label>
                <select
                  {...register('event_type')}
                  className={`form-input ${errors.event_type ? 'error' : ''}`}
                >
                  <option value="">Select event type</option>
                  <option value="conference">Conference</option>
                  <option value="trade_show">Trade Show</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="seminar">Seminar/Workshop</option>
                  <option value="exhibition">Exhibition</option>
                  <option value="other">Other</option>
                </select>
                {errors.event_type && <span className="error-message">{errors.event_type.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="event_size">Expected Attendees *</label>
                <select
                  {...register('event_size')}
                  className={`form-input ${errors.event_size ? 'error' : ''}`}
                >
                  <option value="">Select expected size</option>
                  <option value="50-100">50-100</option>
                  <option value="100-500">100-500</option>
                  <option value="500-1000">500-1000</option>
                  <option value="1000-5000">1000-5000</option>
                  <option value="5000+">5000+</option>
                </select>
                {errors.event_size && <span className="error-message">{errors.event_size.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="event_date">Planned Event Date</label>
                <input
                  {...register('event_date')}
                  type="date"
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-prev" onClick={prevStep}>
                <i className="fas fa-arrow-left"></i> Previous
              </button>
              <button type="button" className="btn-next" onClick={nextStep}>
                Next Step <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="form-step">
            <h3>Additional Information</h3>
            <div className="form-group full">
              <label htmlFor="message">Additional Information</label>
              <textarea
                {...register('message')}
                className={`form-input form-textarea ${errors.message ? 'error' : ''}`}
                rows="4"
                placeholder="Tell us more about your event requirements and any specific features you're interested in..."
              />
              {errors.message && <span className="error-message">{errors.message.message}</span>}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-prev" onClick={prevStep}>
                <i className="fas fa-arrow-left"></i> Previous
              </button>
              <button type="submit" className="btn-submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-rocket"></i>
                    Schedule My Demo
                  </>
                )}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (submitSuccess) {
    return (
      <div className="success-overlay">
        <div className="success-message">
        <div className="success-icon">
          <i className="fas fa-check-circle"></i>
        </div>
        <h3>Demo Request Submitted!</h3>
        <p>Thank you for your interest. We'll be in touch within 24 hours to schedule your personalized demo.</p>
        
        {/* Notification Status */}
        {notificationResults && (
          <div className="notification-status">
            <h4>📧 Notifications Sent</h4>
            <div className="notification-grid">
              <div className="notification-item">
                <div className={`status-indicator ${notificationResults.requesterEmail ? 'success' : 'error'}`}></div>
                <span>Confirmation Email to You</span>
              </div>
              <div className="notification-item">
                <div className={`status-indicator ${notificationResults.salesTeamEmail ? 'success' : 'error'}`}></div>
                <span>Notification to Sales Team</span>
              </div>
              {notificationResults && (
                <div className="notification-item">
                  <div className={`status-indicator ${notificationResults.requesterSMS ? 'success' : 'error'}`}></div>
                  <span>SMS Confirmation</span>
                </div>
              )}
              <div className="notification-item">
                <div className={`status-indicator ${notificationResults.salesTeamSMS ? 'success' : 'error'}`}></div>
                <span>SMS Alert to Sales</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Error Display */}
        {notificationErrors.length > 0 && (
          <div className="notification-errors">
            <h4>⚠️ Notification Issues</h4>
            <ul>
              {notificationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
        
        <div className="success-actions">
          <button 
            className="btn-primary" 
            onClick={() => {
              setSubmitSuccess(false);
              setCurrentStep(1);
            }}
          >
            Request Another Demo
          </button>
          
          <button 
            className="btn-secondary" 
            onClick={() => {
              setSubmitSuccess(false);
              setCurrentStep(1);
              // Scroll to top of page
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            Go Back to Home
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="react-landing-page">
      {/* Modern Hero Section */}
      <section 
        className={`hero ${isVisible ? 'visible' : ''}`}
        ref={heroRef}
        style={{
          transform: `translateY(${scrollY * 0.3}px)`,
          '--mouse-x': `${mousePosition.x}%`,
          '--mouse-y': `${mousePosition.y}%`
        }}
      >
        {/* Animated Background */}
        <div className="hero-bg">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">🚀</span>
              <span>Trusted by {animatedStats.organizers}+ Event Organizers</span>
            </div>
            
            <h1 className="hero-title">
              Transform Your Events
              <span className="hero-title-highlight">With Intelligence</span>
            </h1>
            
            <p className="hero-subtitle">
              The future of event management is here. AI-powered registration, 
              instant check-ins, and real-time analytics that actually work.
            </p>
            
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">{animatedStats.events}+</div>
                <div className="stat-label">Events Managed</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{animatedStats.attendees.toLocaleString()}+</div>
                <div className="stat-label">Attendees Served</div>
              </div>
            </div>
            
            <div className="hero-actions">
              <a href="#features" className="btn btn-primary">
                <span>Explore Features</span>
                <i className="fas fa-arrow-right"></i>
              </a>
              <a href="#demo" className="btn btn-secondary">
                <span>See Demo</span>
                <i className="fas fa-play"></i>
              </a>
            </div>
          </div>
          
          <div className="hero-visual">
            <div className="feature-showcase">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`feature-card ${index === activeFeature ? 'active' : ''}`}
                  style={{ '--feature-color': feature.color }}
                >
                  <div className="feature-icon">{feature.icon}</div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features" ref={featuresRef}>
        <div className="features-container">
          <div className="section-header">
            <div className="section-badge">Features</div>
            <h2 className="section-title">Everything You Need</h2>
            <p className="section-subtitle">
              Powerful tools designed for modern event organizers who demand excellence.
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-users"></i>
              </div>
              <h3 className="feature-title">Attendee Management</h3>
              <p className="feature-description">
                Complete control over your attendee database with advanced filtering, 
                segmentation, and communication tools.
              </p>
              <ul className="feature-list">
                <li>Bulk import and export</li>
                <li>Custom registration forms</li>
                <li>Email marketing integration</li>
                <li>Attendee tracking</li>
              </ul>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-ticket-alt"></i>
              </div>
              <h3 className="feature-title">Registration System</h3>
              <p className="feature-description">
                Streamlined registration process with customizable forms, 
                payment processing, and automated confirmations.
              </p>
              <ul className="feature-list">
                <li>Multi-step registration</li>
                <li>Payment gateway integration</li>
                <li>Automated confirmations</li>
                <li>Waitlist management</li>
              </ul>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <h3 className="feature-title">Mobile Check-in</h3>
              <p className="feature-description">
                Fast and efficient check-in process using mobile devices 
                with QR code scanning and offline capabilities.
              </p>
              <ul className="feature-list">
                <li>QR code scanning</li>
                <li>Offline functionality</li>
                <li>Multiple check-in points</li>
                <li>Real-time sync</li>
              </ul>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3 className="feature-title">Analytics & Reporting</h3>
              <p className="feature-description">
                Comprehensive analytics and reporting tools to track 
                attendance, engagement, and event performance metrics.
              </p>
              <ul className="feature-list">
                <li>Real-time dashboards</li>
                <li>Custom reports</li>
                <li>Export capabilities</li>
                <li>Performance insights</li>
              </ul>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="feature-title">Security & Compliance</h3>
              <p className="feature-description">
                Enterprise-grade security with data encryption, 
                GDPR compliance, and secure payment processing.
              </p>
              <ul className="feature-list">
                <li>Data encryption</li>
                <li>GDPR compliance</li>
                <li>Secure payments</li>
                <li>Access controls</li>
              </ul>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-headset"></i>
              </div>
              <h3 className="feature-title">24/7 Support</h3>
              <p className="feature-description">
                Dedicated support team available around the clock 
                to ensure your events run smoothly without interruption.
              </p>
              <ul className="feature-list">
                <li>Live chat support</li>
                <li>Phone assistance</li>
                <li>Email support</li>
                <li>Knowledge base</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Subtle Demo Section */}
      <section id="demo" className="demo">
        <div className="demo-container">
          <div className="demo-header">
            <div className="section-badge">Ready to Get Started?</div>
            <h2 className="section-title">See It In Action</h2>
            <p className="section-subtitle">
              Schedule a quick demo and see how we can transform your event management.
            </p>
          </div>
          
          <div className="demo-form">
            <form onSubmit={handleSubmit(onSubmit)}>
              {renderStepIndicator()}
              {renderFormStep()}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
